import { quotationKeys } from 'components/quotations/quotationKeys';
import { format } from 'date-fns';
import { Quotation } from 'types/quotation';

/**
 * Mérési típus érték formázása emberileg olvasható címkére
 * @param value A formázandó érték (negyedoras, profil, stb.)
 * @returns Emberileg olvasható címke
 */
const formatMeasurementType = (value: string): string => {
  const mapping: Record<string, string> = {
    'negyedoras': 'AMR',
    'negyedoras_hmke': 'AMR  - HMKE',
    'profil': 'SLP',
    'tavmert_slp': 'SLP (KIF4) - Távmért',
    'profil_hmke': 'SLP (profil alapú) - HMKE',
    'tavmert_slp_hmke': 'SLP (KIF4) - Távmért - HMKE',
    'AMR': 'AMR',
    'SLP': 'SLP'
  };
  return mapping[value] || value || '';
};

/**
 * Fogyasztás mező formázása ezres tagolással
 * @param value A formázandó érték
 * @returns Formázott érték ezres tagolással
 */
const formatConsumption = (value: string): string => {
  if (!value) return '';
  
  // Eltávolítjuk a nem-szám karaktereket, kivéve a tizedespontot
  const cleanValue = value.replace(/[^0-9.]/g, '');
  
  // Szétválasztjuk a tizedes rész előtti és utáni részre
  const parts = cleanValue.split('.');
  
  // Formázzuk az egész részt ezres tagolással
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  
  // Összerakjuk az eredményt
  return parts.length > 1 ? parts.join('.') : parts[0];
};

/**
 * Az árajánlat adatokat a dokumentumgeneráláshoz használható kulcs-érték párokká alakítja
 * 
 * @param quotation Az árajánlat objektum
 * @returns A kulcs-érték párok objektuma, ami a dokumentumgeneráláshoz használható
 */
export const quotationToKeyValuePairs = (quotation: Quotation): Record<string, string> => {
  // Lemásoljuk a sablont, hogy ne módosítsuk az eredetit
  const mappedData = { ...quotationKeys } as Record<string, string>;

  if (!quotation) return mappedData;

  console.log('quotationToKeyValuePairs input object:', quotation);
  console.log('quotation.representativeName:', quotation.representativeName);

  // Alapadatok
  mappedData['Ajánlat száma'] = quotation.quotationNumber || quotation.number || '';
  // Szerződés szám kezdetben üres, majd a QuotationToContract komponens beállítja
  mappedData['Szerződés száma'] = '';
  mappedData['Dátum'] = quotation.date || format(new Date(), 'yyyy.MM.dd.');
  
  // Ajánlat érvényessége formázása datetime formátumból
  if (quotation.validUntil) {
    // Ha ISO datetime formátumban van
    if (typeof quotation.validUntil === 'string' && quotation.validUntil.includes('T')) {
      const date = new Date(quotation.validUntil);
      if (!isNaN(date.getTime())) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        mappedData['Ajánlat érvényessége'] = `${year}.${month}.${day}. ${hours}:${minutes}`;
      } else {
        mappedData['Ajánlat érvényessége'] = quotation.validUntil;
      }
    } else {
      // Ellenőrizzük, hogy a dátum string megfelelő formátumú-e
      const isoMatch = quotation.validUntil.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (isoMatch) {
        // YYYY-MM-DD formátum átalakítása YYYY.MM.DD. formátumra
        const [_, year, month, day] = isoMatch;
        mappedData['Ajánlat érvényessége'] = `${year}.${month}.${day}.`;
      } else if (quotation.validUntil.match(/^\d{4}\.\d{2}\.\d{2}$/)) {
        // YYYY.MM.DD formátum, de nincs pont a végén
        mappedData['Ajánlat érvényessége'] = `${quotation.validUntil}.`;
      } else if (quotation.validUntil.match(/^\d{4}\.\d{2}\.\d{2}\.$/)) {
        // Már helyes formátumban van
        mappedData['Ajánlat érvényessége'] = quotation.validUntil;
      } else {
        // Ha nem tudjuk értelmezni, az eredeti értéket használjuk
        mappedData['Ajánlat érvényessége'] = quotation.validUntil;
      }
    }
  } else {
    mappedData['Ajánlat érvényessége'] = '';
  }
  
  mappedData['Fizetési határidő'] = quotation.paymentDue ? `${quotation.paymentDue} nap` : '';

  // Cég adatok
  mappedData['Cég megnevezése'] = quotation.companyName || '';
  mappedData['Cég címe'] = quotation.companyAddress || '';
  mappedData['Cég adószáma'] = quotation.taxNumber || '';
  mappedData['Cégjegyzékszám'] = quotation.registrationNumber || '';
  mappedData['Képviselő neve'] = quotation.representativeName || '';
  mappedData['Kapcsolattartó neve'] = quotation.contactName || '';

  // Dátum formázás függvény
  const formatDateString = (dateStr: string | undefined): string => {
    if (!dateStr) return '';
    
    try {
      // ISO datetime formátum kezelése (YYYY-MM-DDTHH:MM)
      if (dateStr.includes('T')) {
        const dateObj = new Date(dateStr);
        if (isNaN(dateObj.getTime())) return dateStr;
        
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        
        return `${year}.${month}.${day}.`;
      }
      
      // YYYY-MM-DD formátum átalakítása YYYY.MM.DD. formátumra
      const isoDateMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (isoDateMatch) {
        const [_, year, month, day] = isoDateMatch;
        return `${year}.${month}.${day}.`;
      }
      
      // YYYY/MM/DD formátum átalakítása YYYY.MM.DD. formátumra
      const slashDateMatch = dateStr.match(/^(\d{4})\/(\d{2})\/(\d{2})$/);
      if (slashDateMatch) {
        const [_, year, month, day] = slashDateMatch;
        return `${year}.${month}.${day}.`;
      }
      
      // YYYY.MM.DD formátum ellenőrzése - ha nincs a végén pont, hozzáadjuk
      const dotDateMatch = dateStr.match(/^(\d{4})\.(\d{2})\.(\d{2})$/);
      if (dotDateMatch) {
        return `${dateStr}.`;
      }
      
      // Ha már helyes formátumban van (YYYY.MM.DD.)
      if (/^\d{4}\.\d{2}\.\d{2}\.$/.test(dateStr)) {
        return dateStr;
      }
      
      // Egyéb esetben megpróbáljuk Date objektumként értelmezni
      try {
        const d = new Date(dateStr);
        if (!isNaN(d.getTime())) {
          return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}.`;
        }
      } catch (parseError) {
        console.error('Date parsing error:', parseError);
      }
      
      // Ha egyik formátum sem ismerhető fel, visszaadjuk az eredeti értéket
      return dateStr;
    } catch (error) {
      console.error('Invalid date format:', dateStr, error);
      return dateStr || '';
    }
  };

  // Szerződés időszak formázott dátumokkal
  mappedData['Szerződés kezdete'] = formatDateString(quotation.contractStart);
  mappedData['Szerződés vége'] = formatDateString(quotation.contractEnd);

  // Ár - både ajánlati és szerződéses mezők
  if (quotation.price) {
    mappedData['Ajánlati ár'] = formatConsumption(quotation.price) || '';
    mappedData['Szerződéses ár'] = formatConsumption(quotation.price) || '';
    mappedData['Ajánlati ár HUF/kWh-EUR/MWh'] = quotation.priceUnit || '';
    mappedData['Ár egység'] = quotation.priceUnit || '';
    // Közvetlenül is tároljuk a priceUnit értéket, hogy könnyebben hozzáférjünk
    mappedData['priceUnit'] = quotation.priceUnit || '';
    
    console.log('Árajánlat priceUnit beállítása:', quotation.priceUnit);
  }

  // Fogyasztás - ezres tagolással formázva
  if (quotation.consumption) {
    mappedData['Fogyasztás kWh'] = formatConsumption(quotation.consumption) || '';
    mappedData['Fogyasztás'] = formatConsumption(quotation.consumption) || '';
  }

  // KÁT adatok
  if (quotation.kat) {
    mappedData['KÁT'] = '';
    mappedData['KÁT szöveg'] = quotation.kat.text || '';
  }

  // POD adatok
  if (quotation.pod) {
    mappedData['POD'] = quotation.pod.id || '';
    mappedData['Fogy.hely cím'] = quotation.pod.address || '';
    mappedData['Irányítószám'] = quotation.pod.postalCode || '';
    mappedData['Város'] = quotation.pod.city || '';
    mappedData['Utca, házszám'] = quotation.pod.street || '';
    mappedData['Távmért / Profilos'] = formatMeasurementType(quotation.pod.measurementType) || '';
    mappedData['Mérő típusa'] = quotation.pod.meteringType || ''; // AMR vagy SLP
    mappedData['Teljesítmény (kW)'] = quotation.pod?.power || '';
    mappedData['Profil- szám'] = quotation.pod?.profileNumber || '';
    mappedData['MÉF / Éves mennyiség (kWh)'] = formatConsumption(quotation.pod.annualConsumption) || '';
  }

  // További POD adatok
  if (quotation.additionalPods && quotation.additionalPods.length > 0) {
    for (let i = 0; i < quotation.additionalPods.length; i++) {
      const podNumber = i + 2; // POD 2-től kezdve
      const pod = quotation.additionalPods[i];

      mappedData[`POD ${podNumber}`] = pod.id || '';
      mappedData[`Fogy.hely cím ${podNumber}`] = pod.address || '';
      mappedData[`Irányítószám ${podNumber}`] = pod.postalCode || '';
      mappedData[`Város ${podNumber}`] = pod.city || '';
      mappedData[`Utca, házszám ${podNumber}`] = pod.street || '';
      mappedData[`Távmért / Profilos ${podNumber}`] = formatMeasurementType(pod.measurementType) || '';
      mappedData[`Mérő típusa ${podNumber}`] = pod.meteringType || ''; // AMR vagy SLP
      mappedData[`Teljesítmény (kW) ${podNumber}`] = pod.power || '';
      mappedData[`Profil- szám ${podNumber}`] = pod.profileNumber || '';
      mappedData[`MÉF ${podNumber} / Éves mennyiség (kWh)`] = formatConsumption(pod.annualConsumption) || '';
    }
  }

  // Dinamikus POD táblázat generálása
  mappedData['POD_TABLE'] = generatePodTable(quotation);

  console.log('mappedData complete:', mappedData);
  console.log('mappedData[\'Képviselő neve\']:', mappedData['Képviselő neve']);

  return mappedData;
};

/**
 * Dinamikus POD táblázatot generál az összes POD-ból
 * 
 * @param quotation Az árajánlat objektum
 * @returns Word dokumenetumban használható HTML-szerű táblázat
 */
const generatePodTable = (quotation: Quotation): string => {
  if (!quotation || (!quotation.pod && (!quotation.additionalPods || quotation.additionalPods.length === 0))) {
    return '';
  }

  // Összegyűjtjük az összes POD-ot
  const allPods = [];
  if (quotation.pod) {
    allPods.push(quotation.pod);
  }
  
  if (quotation.additionalPods && quotation.additionalPods.length > 0) {
    allPods.push(...quotation.additionalPods);
  }

  // Word XML táblázat fejléc
  let tableXml = `
    <w:tbl>
      <w:tblPr>
        <w:tblStyle w:val="TableGrid"/>
        <w:tblW w:w="0" w:type="auto"/>
        <w:tblLook w:val="04A0" w:firstRow="1" w:lastRow="0" w:firstColumn="1" w:lastColumn="0" w:noHBand="0" w:noVBand="1"/>
        <w:tblBorders>
          <w:top w:val="single" w:sz="4" w:space="0" w:color="auto"/>
          <w:left w:val="single" w:sz="4" w:space="0" w:color="auto"/>
          <w:bottom w:val="single" w:sz="4" w:space="0" w:color="auto"/>
          <w:right w:val="single" w:sz="4" w:space="0" w:color="auto"/>
          <w:insideH w:val="single" w:sz="4" w:space="0" w:color="auto"/>
          <w:insideV w:val="single" w:sz="4" w:space="0" w:color="auto"/>
        </w:tblBorders>
      </w:tblPr>
      <w:tblGrid>
        <w:gridCol w:w="2000"/>
        <w:gridCol w:w="2000"/>
        <w:gridCol w:w="1500"/>
        <w:gridCol w:w="2000"/>
      </w:tblGrid>
      <!-- Table header -->
      <w:tr>
        <w:trPr>
          <w:tblHeader/>
        </w:trPr>
        <w:tc>
          <w:tcPr><w:shd w:fill="DDDDDD" w:val="clear"/></w:tcPr>
          <w:p><w:r><w:rPr><w:b/><w:sz w:val="18"/><w:szCs w:val="18"/></w:rPr><w:t>POD</w:t></w:r></w:p>
        </w:tc>
        <w:tc>
          <w:tcPr><w:shd w:fill="DDDDDD" w:val="clear"/></w:tcPr>
          <w:p><w:r><w:rPr><w:b/><w:sz w:val="18"/><w:szCs w:val="18"/></w:rPr><w:t>Fogyasztási hely címe</w:t></w:r></w:p>
        </w:tc>
        <w:tc>
          <w:tcPr><w:shd w:fill="DDDDDD" w:val="clear"/></w:tcPr>
          <w:p><w:r><w:rPr><w:b/><w:sz w:val="18"/><w:szCs w:val="18"/></w:rPr><w:t>Mérési mód</w:t></w:r></w:p>
        </w:tc>
        <w:tc>
          <w:tcPr><w:shd w:fill="DDDDDD" w:val="clear"/></w:tcPr>
          <w:p><w:r><w:rPr><w:b/><w:sz w:val="18"/><w:szCs w:val="18"/></w:rPr><w:t>Éves fogyasztás (kWh)</w:t></w:r></w:p>
        </w:tc>
      </w:tr>`;

  // Táblázat sorok
  allPods.forEach(pod => {
    // Ellenőrizzük, hogy mi a POD és mi a cím
    // Ha a pod.id olyan formátumú, mint egy cím (nem tartalmaz elég számot) és 
    // a pod.address viszont HU-val kezdődő POD azonosító, akkor felcserélődtek
    const isPodSwapped = 
      pod.id && pod.address && 
      (
        (pod.id.includes(' ') && pod.address.includes('HU')) || 
        (!pod.id.includes('-') && pod.address && pod.address.includes('-'))
      );
    
    const podId = isPodSwapped ? pod.address : pod.id;
    const podAddress = isPodSwapped ? pod.id : pod.address;
    
    tableXml += `
      <w:tr>
        <w:tc>
          <w:p><w:r><w:rPr><w:sz w:val="18"/><w:szCs w:val="18"/></w:rPr><w:t>${podId || ''}</w:t></w:r></w:p>
        </w:tc>
        <w:tc>
          <w:p><w:r><w:rPr><w:sz w:val="18"/><w:szCs w:val="18"/></w:rPr><w:t>${podAddress || ''}</w:t></w:r></w:p>
        </w:tc>
        <w:tc>
          <w:p><w:r><w:rPr><w:sz w:val="18"/><w:szCs w:val="18"/></w:rPr><w:t>${formatMeasurementType(pod.measurementType) || ''}</w:t></w:r></w:p>
        </w:tc>
        <w:tc>
          <w:p><w:r><w:rPr><w:sz w:val="18"/><w:szCs w:val="18"/></w:rPr><w:t>${formatConsumption(pod.annualConsumption) || ''}</w:t></w:r></w:p>
        </w:tc>
      </w:tr>`;
  });

  // Táblázat lezárása
  tableXml += `</w:tbl>`;

  return tableXml;
}; 